import { useSubscriptionDetails } from '@hooks/apiHooks/subscriptionHooks';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { brazeIdentity } from '@services/braze';
import { analytics } from '@utils/analytics';
import { getOrganizationId } from '@utils/getOrganization';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';
import type { OidcUser } from '../types/oidcUser';

/**
 * Runs once at the App level (outside Router) so it survives navigation
 * without remounting. Fetches subscription details, seeds the Redux balance
 * for the session, and identifies the user in analytics and Braze.
 * Balance updates after the initial load flow through WebRTC 'update-balance'.
 */
export const useInitializeUserData = () => {
  const { user, isAuthenticated } = useAuth();
  const { dataChannel, setSecondsBalance, setCreditBalance } =
    useSessionFeatureState();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isInitialRouteRef = useRef(true);

  const orgId = getOrganizationId(user as OidcUser);
  const { data: subscriptionData } = useSubscriptionDetails(
    { organization_id: orgId },
    isAuthenticated,
    '',
  );

  // On navigation, refresh the cached subscription details — but only when
  // no WebRTC session is active. While `dataChannel` is open, balance flows
  // through 'update-balance' events into Redux and API refetches would just
  // race that path.
  useEffect(() => {
    if (isInitialRouteRef.current) {
      isInitialRouteRef.current = false;
      return;
    }
    if (dataChannel) return;
    queryClient.invalidateQueries({ queryKey: ['getSubscriptionDetails'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    // Sync balance from API into Redux only when no WebRTC session is open.
    // During a session, balance flows through 'update-balance' events and
    // Redux is authoritative. Outside a session (initial load, post-disconnect,
    // post-purchase, account switch) the API is the source of truth.
    if (subscriptionData && !dataChannel) {
      const { credit_usage } = subscriptionData;

      const balance =
        credit_usage.subscription_details.balance +
        credit_usage.pack_details.balance;
      setSecondsBalance(balance);

      const credits =
        (credit_usage.subscription_details.creditBalance || 0) +
        (credit_usage.pack_details.creditBalance || 0);
      setCreditBalance(credits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData, dataChannel]);

  useEffect(() => {
    if (user && subscriptionData) {
      const { credit_usage, billingDetails } = subscriptionData;
      const oidcUser = user as OidcUser;
      const params: Record<string, string | number | boolean | string[]> = {
        user_id: oidcUser.profile.sub,
        role: oidcUser.profile.role ?? '',
        is_org_owner: oidcUser.profile.organization?.[0]?.isOwner
          ? 'yes'
          : 'no',
        is_old_music_user: oidcUser.profile.isOldMusicUser ? 'yes' : 'no',
        idp: oidcUser.profile.idp ?? '',
        available_credits:
          credit_usage.subscription_details.creditBalance +
          credit_usage.pack_details.creditBalance,
        billingProvider: billingDetails.provider,
        app_version: '1.0.0',
      };

      if (
        oidcUser.profile.email?.toLowerCase().includes('touchcast') ||
        oidcUser.profile.email?.toLowerCase().includes('napster') ||
        oidcUser.profile.email?.toLowerCase().includes('theinfinitereality')
      ) {
        params.is_employee = 'yes';
      }

      analytics.setUser(oidcUser.profile.sub, params);

      brazeIdentity.identifyUser(user, subscriptionData);
    }
  }, [user, subscriptionData]);
};
